/**
 * 顶部导航栏：腾讯广告 / 巨量广告 / 华为应用商店 / Apple Ads / 荣耀 / VIVO /
 * 百度广告 / 我的资源 八个 Tab + 右上角用户区（CLAUDE.md §功能点 3）。
 *
 * 设计（LEO 规范 §6.2 顶栏 Tab，v1.0）：
 *   - 默认：灰字 + 透明底
 *   - 激活：橙 tint + 橙字 + 轻发光
 *   - Tab 文案左侧带平台品牌图标（单色填充，颜色随 .leo-toptab 的 currentColor
 *     走 灰/亮/橙 三态，不引入额外色值；见下方「平台品牌图标」注释）
 *
 * 激活态判定：各平台壳（PlatformShell）将自身平台 key 作为 `active` 传入，
 * 命中者加 `is-active`；百度/Apple Ads/荣耀/VIVO 落地子页均为「页面内容为空」（详见 App.tsx 路由）；
 * 「我的资源」为跨平台共享资源入口（展业城市等），非品牌 Tab，但图标经用户指定改用微众银行折纸鸟造型（fill 剪影，见下方 IconResources 注释）。
 *
 * 右上角用户区（竖向小栈）：
 *   - 上行：当前用户：xxx
 *   - 下行：[深/浅背景切换按钮] [退出登录]
 *   主题切换按钮点击调 useTheme().toggleTheme()，翻转 <html> 上的 dark 类，
 *   经 index.css 的 :root / .dark 变量集翻转整站背景/顶栏/侧栏/卡片/输入/文字。
 */

import type { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { pushToast } from "../hooks/useToast";
import type { PlatformKey } from "./Sidebar";

export type TopPlatform = PlatformKey;

/*
 * —— 平台品牌图标（顶栏 Tab 文案左侧）——
 *
 * 来源与风格说明：
 *   - 腾讯（QQ 企鹅）/ 华为（花瓣）/ 百度（熊掌）/ Apple（苹果标）/ 巨量（字节跳动标）：
 *     取自 simple-icons v13（CC0 协议的单色品牌轮廓），path 原样内联，无外部依赖；
 *   - VIVO（vivo 字标）：取自 simple-icons 官方 CDN（同 CC0 单色品牌轮廓），path 原样内联；
 *   - 荣耀（荣耀之环）：取自荣耀官网官方 logo 左侧的环形徽标（两段月牙弧、对角两处
 *     断口的圆环），path 原样内联，经 g transform 缩放归一化到 24×24 视窗，无外部依赖；
 *   - LEO §6.8 规定通用 UI 图标用 stroke 风格；品牌标志天然为剪影，故七个平台 Tab
 *     图标统一采用 fill="currentColor" 单色填充，颜色完全由 .leo-toptab 的
 *     currentColor 三态（--text-muted / hover / is-active 橙）驱动，深浅主题自动适配；
 *     「我的资源」非品牌 Tab，但用户指定图标改用微众银行折纸鸟造型，同样 fill=currentColor 单色剪影走三态；
 *   - 图标与文案相邻，属装饰性图形，统一 aria-hidden。
 */
const ICON_CLASS = "h-4 w-4 shrink-0";

/** 腾讯广告：QQ 企鹅剪影 */
const IconTencent = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M21.395 15.035a40 40 0 0 0-.803-2.264l-1.079-2.695c.001-.032.014-.562.014-.836C19.526 4.632 17.351 0 12 0S4.474 4.632 4.474 9.241c0 .274.013.804.014.836l-1.08 2.695a39 39 0 0 0-.802 2.264c-1.021 3.283-.69 4.643-.438 4.673.54.065 2.103-2.472 2.103-2.472 0 1.469.756 3.387 2.394 4.771-.612.188-1.363.479-1.845.835-.434.32-.379.646-.301.778.343.578 5.883.369 7.482.189 1.6.18 7.14.389 7.483-.189.078-.132.132-.458-.301-.778-.483-.356-1.233-.646-1.846-.836 1.637-1.384 2.393-3.302 2.393-4.771 0 0 1.563 2.537 2.103 2.472.251-.03.581-1.39-.438-4.673" />
  </svg>
);

/** 巨量广告：字节跳动剪影（bytedance，simple-icons v13） */
const IconOcean = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M19.8772 1.4685L24 2.5326v18.9426l-4.1228 1.0563V1.4685zm-13.3481 9.428l4.115 1.0641v8.9786l-4.115 1.0642v-11.107zM0 2.572l4.115 1.0642v16.7354L0 21.428V2.572zm17.4553 5.6205v11.107l-4.1228-1.0642V9.2568l4.1228-1.0642z" />
  </svg>
);

/** 华为应用商店：华为花瓣剪影 */
const IconHuawei = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M3.67 6.14S1.82 7.91 1.72 9.78v.35c.08 1.51 1.22 2.4 1.22 2.4 1.83 1.79 6.26 4.04 7.3 4.55 0 0 .06.03.1-.01l.02-.04v-.04C7.52 10.8 3.67 6.14 3.67 6.14zM9.65 18.6c-.02-.08-.1-.08-.1-.08l-7.38.26c.8 1.43 2.15 2.53 3.56 2.2.96-.25 3.16-1.78 3.88-2.3.06-.05.04-.09.04-.09zm.08-.78C6.49 15.63.21 12.28.21 12.28c-.15.46-.2.9-.21 1.3v.07c0 1.07.4 1.82.4 1.82.8 1.69 2.34 2.2 2.34 2.2.7.3 1.4.31 1.4.31.12.02 4.4 0 5.54 0 .05 0 .08-.05.08-.05v-.06c0-.03-.03-.05-.03-.05zM9.06 3.19a3.42 3.42 0 0 0-2.57 3.15v.41c.03.6.16 1.05.16 1.05.66 2.9 3.86 7.65 4.55 8.65.05.05.1.03.1.03a.1.1 0 0 0 .06-.1c1.06-10.6-1.11-13.42-1.11-13.42-.32.02-1.19.23-1.19.23zm8.299 2.27s-.49-1.8-2.44-2.28c0 0-.57-.14-1.17-.22 0 0-2.18 2.81-1.12 13.43.01.07.06.08.06.08.07.03.1-.03.1-.03.72-1.03 3.9-5.76 4.55-8.64 0 0 .36-1.4.02-2.34zm-2.92 13.07s-.07 0-.09.05c0 0-.01.07.03.1.7.51 2.85 2 3.88 2.3 0 0 .16.05.43.06h.14c.69-.02 1.9-.37 3-2.26l-7.4-.25zm7.83-8.41c.14-2.06-1.94-3.97-1.94-3.98 0 0-3.85 4.66-6.67 10.8 0 0-.03.08.02.13l.04.01h.06c1.06-.53 5.46-2.77 7.28-4.54 0 0 1.15-.93 1.21-2.42zm1.52 2.14s-6.28 3.37-9.52 5.55c0 0-.05.04-.03.11 0 0 .03.06.07.06 1.16 0 5.56 0 5.67-.02 0 0 .57-.02 1.27-.29 0 0 1.56-.5 2.37-2.27 0 0 .73-1.45.17-3.14z" />
  </svg>
);

/** 百度广告：百度熊掌剪影 */
const IconBaidu = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M9.154 0C7.71 0 6.54 1.658 6.54 3.707c0 2.051 1.171 3.71 2.615 3.71 1.446 0 2.614-1.659 2.614-3.71C11.768 1.658 10.6 0 9.154 0zm7.025.594C14.86.58 13.347 2.589 13.2 3.927c-.187 1.745.25 3.487 2.179 3.735 1.933.25 3.175-1.806 3.422-3.364.252-1.555-.995-3.364-2.362-3.674a1.218 1.218 0 0 0-.261-.03zM3.582 5.535a2.811 2.811 0 0 0-.156.008c-2.118.19-2.428 3.24-2.428 3.24-.287 1.41.686 4.425 3.297 3.864 2.617-.561 2.262-3.68 2.183-4.362-.125-1.018-1.292-2.773-2.896-2.75zm16.534 1.753c-2.308 0-2.617 2.119-2.617 3.616 0 1.43.121 3.425 2.988 3.362 2.867-.063 2.553-3.238 2.553-3.988 0-.745-.62-2.99-2.924-2.99zm-8.264 2.478c-1.424.014-2.708.925-3.323 1.947-1.118 1.868-2.863 3.05-3.112 3.363-.25.309-3.61 2.116-2.864 5.42.746 3.301 3.365 3.237 3.365 3.237s1.93.19 4.171-.31c2.24-.495 4.17.123 4.17.123s5.233 1.748 6.665-1.616c1.43-3.364-.808-5.109-.808-5.109s-2.99-2.306-4.736-4.798c-1.072-1.665-2.348-2.268-3.528-2.257zm-2.234 3.84l1.542.024v8.197H7.758c-1.47-.291-2.055-1.292-2.13-1.462-.072-.173-.488-.976-.268-2.343.635-2.049 2.447-2.196 2.447-2.196h1.81zm3.964 2.39v3.881c.096.413.612.488.612.488h1.614v-4.343h1.689v5.782h-3.915c-1.517-.39-1.59-1.465-1.59-1.465v-4.317zm-5.458 1.147c-.66.197-.978.708-1.05.928-.076.22-.247.78-.1 1.269.294 1.095 1.248 1.144 1.248 1.144h1.37v-3.34z" />
  </svg>
);

/** Apple Ads：Apple 标剪影 */
const IconApple = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
  </svg>
);

/** 荣耀：荣耀之环剪影（荣耀官网官方 logo 左侧的环形徽标，两段月牙弧） */
const IconHonor = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    {/* 源坐标系为 0-42 方框（官网 logo viewBox），scale(24/42)≈0.5714 归一化到本组件 24×24 视窗 */}
    <g transform="scale(0.5714)">
      <path d="M34.8653 5.2339L27.2565 9.62849C22.3267 6.9097 16.0028 7.64189 11.8218 11.8237C7.64086 16.0055 6.91021 22.3307 9.62706 27.2615L5.23335 34.8718C-2.03818 26.6245 -1.73164 14.0343 6.15016 6.1509C14.0306 -1.73109 26.6182 -2.03909 34.8653 5.2339Z" />
      <path d="M36.7589 7.12817C44.0305 15.3756 43.7239 27.9657 35.8421 35.8491C27.9617 43.7311 15.3741 44.0391 7.12695 36.7661L14.7358 32.3715C19.6656 35.0903 25.9895 34.3581 30.1705 30.1763C34.3514 25.9945 35.0821 19.6694 32.3652 14.7386L36.7589 7.12817Z" />
    </g>
  </svg>
);

/** VIVO：vivo 官方字标剪影（simple-icons） */
const IconVivo = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    <path d="M19.604 14.101c-1.159 0-1.262-.95-1.262-1.24 0-.29.103-1.242 1.262-1.242h2.062c1.16 0 1.263.951 1.263 1.242 0 .29-.104 1.24-1.263 1.24m-2.062-3.527c-2.142 0-2.333 1.752-2.333 2.287 0 .535.19 2.286 2.333 2.286h2.062c2.143 0 2.334-1.751 2.334-2.286 0-.535-.19-2.287-2.334-2.287m-5.477.107c-.286 0-.345.05-.456.213-.11.164-2.022 3.082-2.022 3.082-.06.09-.126.126-.206.126-.08 0-.145-.036-.206-.126 0 0-1.912-2.918-2.022-3.082-.11-.164-.17-.213-.456-.213h-.668c-.154 0-.224.12-.127.267l2.283 3.467c.354.521.614.732 1.196.732s.842-.21 1.196-.732l2.284-3.467c.096-.146.026-.267-.128-.267m-8.876.284c0-.203.08-.284.283-.284h.505c.203 0 .283.08.283.283v3.9c0 .202-.08.283-.283.283h-.505c-.203 0-.283-.08-.283-.283zm-1.769-.285c-.287 0-.346.05-.456.213-.11.164-2.022 3.082-2.022 3.082-.061.09-.126.126-.206.126-.08 0-.145-.036-.206-.126 0 0-1.912-2.918-2.023-3.082-.11-.164-.169-.213-.455-.213H.175c-.171 0-.224.12-.127.267l2.283 3.467c.355.521.615.732 1.197.732.582 0 .842-.21 1.196-.732l2.283-3.467c.097-.146.044-.267-.127-.267m1.055-.893c-.165-.164-.165-.295 0-.46l.351-.351c.165-.165.296-.165.46 0l.352.351c.165.165.165.296 0 .46l-.352.352c-.164.165-.295.165-.46 0z" />
  </svg>
);

/*
 * 我的资源：微众银行折纸鸟造型（用户指定）。
 * 依据微众银行官网 og 图中的几何折纸鸟 mark（头尖朝上、右翼长边、左侧双翼尖、
 * 尾尖朝下的折纸剪影）手工简化的多边形坐标：外轮廓挖空中部折面孔洞（evenodd），
 * 孔洞上再叠一层 opacity 0.35 的同形折面模拟原 logo 的浅色受光面，
 * 折面中的小菱形为原 logo 的深色翼面（fill=currentColor 全量）。
 * 同样走 .leo-toptab 的 currentColor 三态，深浅主题自动适配。
 */
const IconResources = (
  <svg viewBox="0 0 24 24" className={ICON_CLASS} fill="currentColor" aria-hidden="true">
    {/* 外轮廓（evenodd 挖空中部折面）+ 同形折面低透明度叠层 + 翼面小菱形 */}
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15.3.3 23.8 3.5 23.2 22 12 23.7.5 22.7.5 17.5 2.2 11.3 4.8 7.7 2.1 6.2 5.6 4.4ZM10.6 3.5 15 3.5 17.3 4.7 21 5.8 17.5 7 15.8 8.2 10.9 11.6 9.6 13.9 8.7 17.4 10.4 20.9 11.8 22.1 6.5 20.9 5.1 18.6 4.4 16.3 5.3 11.6 9.1 10.5 8.7 8.1 8.2 7 8.4 5.8Z"
    />
    <path
      opacity={0.35}
      d="M10.6 3.5 15 3.5 17.3 4.7 21 5.8 17.5 7 15.8 8.2 10.9 11.6 9.6 13.9 8.7 17.4 10.4 20.9 11.8 22.1 6.5 20.9 5.1 18.6 4.4 16.3 5.3 11.6 9.1 10.5 8.7 8.1 8.2 7 8.4 5.8Z"
    />
    <path d="M14.7 4.9 16 6 14.7 7.1 13.8 6Z" />
  </svg>
);

/** 顶栏 Tab 配置：平台 key → 跳转路径 + 显示文案 */
const TOP_TABS: { key: TopPlatform; to: string; label: string; icon: ReactNode }[] = [
  { key: "tencent", to: "/tencent", label: "腾讯广告", icon: IconTencent },
  { key: "ocean", to: "/ocean", label: "巨量广告", icon: IconOcean },
  { key: "huawei", to: "/huawei", label: "华为应用商店", icon: IconHuawei },
  { key: "apple", to: "/apple", label: "Apple Ads", icon: IconApple },
  { key: "honor", to: "/honor", label: "荣耀", icon: IconHonor },
  { key: "vivo", to: "/vivo", label: "VIVO", icon: IconVivo },
  { key: "baidu", to: "/baidu", label: "百度广告", icon: IconBaidu },
  { key: "my-resources", to: "/my-resources", label: "我的资源", icon: IconResources },
];

export function TopNav({ active }: { active: TopPlatform }) {
  const navigate = useNavigate();
  const { userId, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  async function handleLogout() {
    await logout();
    pushToast("已登出", "success");
    navigate("/login", { replace: true });
  }

  return (
    <header className="leo-topbar sticky top-0 z-40 flex h-14 items-center justify-between px-5 md:px-8">
      <div className="flex items-center gap-6">
        <NavLink
          to="/tencent"
          className="flex items-center gap-2.5 text-text-primary"
        >
          <span className="grid h-7 w-7 place-items-center rounded-md bg-accent-cta/15 text-accent-cta-hover">
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 11l9-7 9 7" />
              <path d="M5 10v10h14V10" />
            </svg>
          </span>
          <span className="font-display text-[15px] font-semibold tracking-[-0.01em]">
            广告自动化投放系统
          </span>
        </NavLink>

        <nav className="flex items-center gap-1.5">
          {TOP_TABS.map((tab) => (
            <NavLink
              key={tab.key}
              to={tab.to}
              className={`leo-toptab ${active === tab.key ? "is-active" : ""}`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      <div className="flex flex-col items-end gap-0.5">
        <span className="hidden text-[11px] text-text-muted md:inline">
          当前用户：<span className="text-text-strong">{userId ?? "—"}</span>
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="leo-btn-ghost"
            title={isDark ? "当前深色，点击切换为浅色" : "当前浅色，点击切换为深色"}
            aria-label={isDark ? "切换为浅色背景" : "切换为深色背景"}
          >
            {isDark ? (
              /* 深色态：显月亮，点击切浅色（LEO §6.8 内联 SVG，stroke=currentColor） */
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
              </svg>
            ) : (
              /* 浅色态：显太阳，点击切深色 */
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.75}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="leo-btn-ghost"
          >
            退出登录
          </button>
        </div>
      </div>
    </header>
  );
}